import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import MainLayout from './pages/layouts/MainLayout';
import { Loader } from '@f2m3/shared-frontend-ui';
import { OPEN_LINK_PATH_PREFIX } from './api/albumOpenLink';

// Critical path - load immediately (needed for initial render)
import Home from './pages/Home';
import Login from './pages/Login';
import Error404 from './pages/Error404';
import ProtectedRoute from "./components/layout/ProtectedRoute";
import AlbumWallGuard from "./components/layout/AlbumWallGuard";
import AdminRoute from './components/layout/AdminRoute';
import AdminOnlyRoute from './components/layout/AdminOnlyRoute';
import RetouchStaffRoute from './components/layout/RetouchStaffRoute';
import SalesStaffRoute from './components/layout/SalesStaffRoute';
import VerificationGuard from "./components/layout/VerificationGuard";
import GuestCheckoutGuard from "./components/layout/GuestCheckoutGuard";
import AdminIndex from './pages/admin/AdminIndex';
import RedirectToProduction from './pages/admin/RedirectToProduction';

// Lazy-loaded components (loaded on-demand when route is accessed)
const Albums = lazy(() => import('./pages/Albums'));
const Album = lazy(() => import('./pages/Album'));
const MediaPage = lazy(() => import('./pages/Media'));
const StaticPageView = lazy(() => import('./pages/StaticPageView'));
const CookieInfo = lazy(() => import('./pages/CookieInfo'));
const Profile = lazy(() => import('./pages/Profile'));
const Order = lazy(() => import('./pages/Order'));
const Cart = lazy(() => import('./components/order/Cart'));
const ShippingOption = lazy(() => import('./components/order/ShippingOption'));
const PaymentOption = lazy(() => import('./components/order/PaymentOption'));
const OrderReview = lazy(() => import('./components/order/OrderReview'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetails = lazy(() => import('./pages/OrderDetails'));
const Downloads = lazy(() => import('./pages/Downloads'));
const PaypalReturn = lazy(() => import('./pages/paypal/Return'));
const PaypalCancel = lazy(() => import('./pages/paypal/Cancel'));
const StripeReturn = lazy(() => import('./pages/stripe/Return'));
const StripeCancel = lazy(() => import('./pages/stripe/Cancel'));
const BarionReturn = lazy(() => import('./pages/barion/Return'));
const BarionCancel = lazy(() => import('./pages/barion/Cancel'));
const SimplePayReturn = lazy(() => import('./pages/simplepay/Return'));
const AuthTransfer = lazy(() => import('./pages/auth/Transfer'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const VerifyEmailCode = lazy(() => import('./pages/auth/VerifyEmailCode'));
const AdminLayout = lazy(() => import('./pages/layouts/AdminLayout'));
const AdminSuppliers = lazy(() => import('./pages/admin/Suppliers'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminPromotions = lazy(() => import('./pages/admin/AdminPromotions'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminPageEdit = lazy(() => import('./pages/admin/AdminPageEdit'));
const PromotionEdit = lazy(() => import('./pages/admin/PromotionEdit'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const AdminProduction = lazy(() => import('./pages/admin/AdminProduction'));
const AdminProductionDetail = lazy(() => import('./pages/admin/AdminProductionDetail'));
const AdminProductCreate = lazy(() => import('./pages/admin/ProductCreate'));
const AdminProductEdit = lazy(() => import('./pages/admin/ProductEdit'));
const RetouchPage = lazy(() => import('./pages/admin/RetouchPage'));
const RetouchBatchDetail = lazy(() => import('./pages/admin/RetouchBatchDetail'));
const CustomizationPage = lazy(() => import('./pages/admin/CustomizationPage'));
const CustomizationBatchDetail = lazy(() => import('./pages/admin/customizations/CustomizationBatchDetail'));
const CustomizationCatalog = lazy(() => import('./pages/admin/customizations/CustomizationCatalog'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const SettingsOrders = lazy(() => import('./pages/admin/SettingsOrders'));
const AlbumManager = lazy(() => import('./pages/admin/AlbumManager'));
const AlbumTrash = lazy(() => import('./pages/admin/AlbumTrash'));
const AdminMediaVersions = lazy(() => import('./pages/admin/AdminMediaVersions'));
const SettingsLogin = lazy(() => import('./pages/admin/SettingsLogin'));
const AlbumTypesSettings = lazy(() => import('./pages/admin/albumTypes/AlbumTypesSettings'));
const AlbumTypeDetail = lazy(() => import('./pages/admin/albumTypes/AlbumTypeDetail'));
const AdminShipping       = lazy(() => import('./pages/admin/AdminShipping'));
const AdminShippingNew    = lazy(() => import('./pages/admin/AdminShippingNew'));
const AdminShippingEdit   = lazy(() => import('./pages/admin/AdminShippingEdit'));
const AdminPayments       = lazy(() => import('./pages/admin/AdminPayments'));
const AdminPaymentNew     = lazy(() => import('./pages/admin/AdminPaymentNew'));
const AdminPaymentEdit    = lazy(() => import('./pages/admin/AdminPaymentEdit'));
const AdminPrintJobs      = lazy(() => import('./pages/admin/AdminPrintJobs'));
const AdminPrintTemplates = lazy(() => import('./pages/admin/AdminPrintTemplates'));
const AdminInvoices          = lazy(() => import('./pages/admin/Invoices'));
const AdminInvoicingSettings = lazy(() => import('./pages/admin/InvoicingSettings'));
const AdminAnalytics         = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminReports           = lazy(() => import('./pages/admin/AdminReports'));
const AdminBankReconciliation = lazy(() => import('./pages/admin/AdminBankReconciliation'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminEmailAudit = lazy(() => import('./pages/admin/AdminEmailAudit'));
const AdminEmailAuditDetail = lazy(() => import('./pages/admin/AdminEmailAuditDetail'));
const Appearance = lazy(() => import('./pages/admin/Appearance'));
const SettingsShopBasics = lazy(() => import('./pages/admin/SettingsShopBasics'));
const SettingsShopState = lazy(() => import('./pages/admin/SettingsShopState'));
const SettingsWatermark = lazy(() => import('./pages/admin/SettingsWatermark'));
const SettingsLanguages = lazy(() => import('./pages/admin/SettingsLanguages'));
const SettingsEmail = lazy(() => import('./pages/admin/SettingsEmail'));
const OpenAlbum = lazy(() => import('./pages/OpenAlbum'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const RelationsGate = lazy(() => import('./pages/admin/relations/RelationsGate'));
const RelationsPartners = lazy(() => import('./pages/admin/relations/RelationsPartners'));
const RelationsPartnerDetail = lazy(() => import('./pages/admin/relations/RelationsPartnerDetail'));
const RelationsContactDetail = lazy(() => import('./pages/admin/relations/RelationsContactDetail'));
const RelationsFieldsSettings = lazy(() => import('./pages/admin/relations/RelationsFieldsSettings'));
const RelationsTypesSettings = lazy(() => import('./pages/admin/relations/RelationsTypesSettings'));
const RelationsTasks = lazy(() => import('./pages/admin/relations/RelationsTasks'));
const RelationsCalendar = lazy(() => import('./pages/admin/relations/RelationsCalendar'));

// Exported (not just built into the router below) so a structural test can
// walk the plain route objects — e.g. asserting every /admin descendant
// route carries AdminOnlyRoute as an ancestor — without rendering a tree.
// Two pathless staff-facing wrappers sit outside AdminOnlyRoute on purpose:
// SalesStaffRoute (account_sales, with RelationsGate for the feature flag
// below it) and RetouchStaffRoute (account_retoucher).
export const routes: RouteObject[] = [
  {
    path: "/",
    element: <MainLayout />,
    errorElement: <Error404 />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "auth/transfer",
        element: (
          <Suspense fallback={<Loader />}>
            <AuthTransfer />
          </Suspense>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <Suspense fallback={<Loader />}>
            <ForgotPassword />
          </Suspense>
        ),
      },
      {
        path: "reset-password",
        element: (
          <Suspense fallback={<Loader />}>
            <ResetPassword />
          </Suspense>
        ),
      },
      {
        // Link in the shop's verification mail (public; the token is the credential).
        path: "verify-email",
        element: (
          <Suspense fallback={<Loader />}>
            <VerifyEmail />
          </Suspense>
        ),
      },
      {
        path: `${OPEN_LINK_PATH_PREFIX}/:code`,
        element: (
          <Suspense fallback={<Loader />}>
            <OpenAlbum />
          </Suspense>
        ),
      },
      {
        // Album-notification unsubscribe link (public; the token is the credential).
        path: "unsubscribe/:token",
        element: (
          <Suspense fallback={<Loader />}>
            <Unsubscribe />
          </Suspense>
        ),
      },
      {
        path: "page/:slug",
        element: (
          <Suspense fallback={<Loader />}>
            <StaticPageView />
          </Suspense>
        ),
      },
      {
        path: "cookies",
        element: (
          <Suspense fallback={<Loader />}>
            <CookieInfo />
          </Suspense>
        ),
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            // Outside AlbumWallGuard: that guard sends a pending user here.
            path: "verify-email/code",
            element: (
              <Suspense fallback={<Loader />}>
                <VerifyEmailCode />
              </Suspense>
            ),
          },
          {
            element: <AlbumWallGuard />,
            children: [
              {
                path: "albums",
                element: (
                  <Suspense fallback={<Loader />}>
                    <Albums />
                  </Suspense>
                )
              },
              {
                path: "albums/:albumId",
                element: (
                  <Suspense fallback={<Loader />}>
                    <Album />
                  </Suspense>
                )
              },
              {
                path: "albums/:albumId/:picId",
                element: (
                  <Suspense fallback={<Loader />}>
                    <MediaPage />
                  </Suspense>
                )
              },
              {
                // Account-only pages: a user who still has to confirm their email
                // is sent to /verify-email/code (useVerificationWall).
                element: <VerificationGuard />,
                children: [
                {
                  path: "profile",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <Profile />
                    </Suspense>
                  )
                },
                {
                  path: "orders",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <Orders />
                    </Suspense>
                  )
                },
                {
                  path: "orders/paypal/return",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <PaypalReturn />
                    </Suspense>
                  )
                },
                {
                  path: "orders/paypal/cancel",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <PaypalCancel />
                    </Suspense>
                  )
                },
                {
                  path: "orders/stripe/return",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <StripeReturn />
                    </Suspense>
                  )
                },
                {
                  path: "orders/stripe/cancel",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <StripeCancel />
                    </Suspense>
                  )
                },
                {
                  path: "orders/barion/return",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <BarionReturn />
                    </Suspense>
                  )
                },
                {
                  path: "orders/barion/cancel",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <BarionCancel />
                    </Suspense>
                  )
                },
                {
                  path: "orders/simplepay/return",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <SimplePayReturn />
                    </Suspense>
                  )
                },
                {
                  path: "orders/:orderId",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <OrderDetails />
                    </Suspense>
                  )
                },
                {
                  path: "downloads",
                  element: (
                    <Suspense fallback={<Loader />}>
                      <Downloads />
                    </Suspense>
                  )
                }
                ],
              },
              {
                path: "order",
                element: (
                  <Suspense fallback={<Loader />}>
                    <Order />
                  </Suspense>
                ),
                children: [
                  {
                    path: "cart",
                    element: (
                      <Suspense fallback={<Loader />}>
                        <Cart />
                      </Suspense>
                    )
                  },
                  {
                    // GuestCheckoutGuard prevents guest users from accessing checkout steps
                    element: <GuestCheckoutGuard />,
                    children: [
                      {
                        path: "shipping",
                        element: (
                          <Suspense fallback={<Loader />}>
                            <ShippingOption />
                          </Suspense>
                        )
                      },
                      {
                        path: "payment",
                        element: (
                          <Suspense fallback={<Loader />}>
                            <PaymentOption />
                          </Suspense>
                        )
                      },
                      {
                        path: "review",
                        element: (
                          <Suspense fallback={<Loader />}>
                            <OrderReview />
                          </Suspense>
                        )
                      }
                    ]
                  }
                ]
              }
            ],
          },
        ]
      }
    ],
  },
  {
    path: "/admin",
    element: <AdminRoute />,
    errorElement: <Error404 />,
    children: [
      {
        element: (
          <Suspense fallback={<Loader />}>
            <AdminLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <AdminIndex />,
          },
          {
            // Staff-facing (account_sales works here). SalesStaffRoute admits
            // SALES_STAFF and gives a retoucher Forbidden; RelationsGate then
            // gates the branch on the `relations` feature flag, and the
            // settings sub-branch below is admin-only.
            element: <SalesStaffRoute />,
            children: [
              {
                path: "relations",
                element: (
                  <Suspense fallback={<Loader />}>
                    <RelationsGate />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Loader />}>
                        <RelationsCalendar />
                      </Suspense>
                    ),
                  },
                  {
                    path: "partners",
                    element: (
                      <Suspense fallback={<Loader />}>
                        <RelationsPartners />
                      </Suspense>
                    ),
                  },
                  {
                    path: "partners/:id",
                    element: (
                      <Suspense fallback={<Loader />}>
                        <RelationsPartnerDetail />
                      </Suspense>
                    ),
                  },
                  {
                    path: "contacts/:id",
                    element: (
                      <Suspense fallback={<Loader />}>
                        <RelationsContactDetail />
                      </Suspense>
                    ),
                  },
                  {
                    path: "tasks",
                    element: (
                      <Suspense fallback={<Loader />}>
                        <RelationsTasks />
                      </Suspense>
                    ),
                  },
                  {
                    element: <AdminOnlyRoute />,
                    children: [
                      {
                        path: "settings/fields",
                        element: (
                          <Suspense fallback={<Loader />}>
                            <RelationsFieldsSettings />
                          </Suspense>
                        ),
                      },
                      {
                        path: "settings/types",
                        element: (
                          <Suspense fallback={<Loader />}>
                            <RelationsTypesSettings />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            // Staff-facing (account_retoucher works here): the retouch and
            // Variants work surfaces. RetouchStaffRoute admits RETOUCH_STAFF;
            // the Variants catalog stays under AdminOnlyRoute below.
            element: <RetouchStaffRoute />,
            children: [
              {
                path: "retouch",
                element: (
                  <Suspense fallback={<Loader />}>
                    <RetouchPage />
                  </Suspense>
                ),
              },
              { path: "retouch/active", element: <Navigate to="/admin/retouch" replace /> },
              { path: "retouch/orders", element: <Navigate to="/admin/retouch?tab=orders" replace /> },
              { path: "retouch/pics", element: <Navigate to="/admin/retouch?tab=pics" replace /> },
              {
                path: "retouch/batches/:id",
                element: (
                  <Suspense fallback={<Loader />}>
                    <RetouchBatchDetail />
                  </Suspense>
                ),
              },
              {
                path: "customizations",
                element: (
                  <Suspense fallback={<Loader />}>
                    <CustomizationPage />
                  </Suspense>
                ),
              },
              { path: "customizations/active", element: <Navigate to="/admin/customizations" replace /> },
              { path: "customizations/pending", element: <Navigate to="/admin/customizations" replace /> },
              {
                path: "customizations/batches/:id",
                element: (
                  <Suspense fallback={<Loader />}>
                    <CustomizationBatchDetail />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <AdminOnlyRoute />,
            children: [
              {
                path: "users",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminUsers />
                  </Suspense>
                ),
              },
              {
                path: "users/:id",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminUserDetail />
                  </Suspense>
                ),
              },
              {
                path: "messages",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminMessages />
                  </Suspense>
                ),
              },
              {
                path: "messages/:userId",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminMessages />
                  </Suspense>
                ),
              },
              {
                path: "albums",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AlbumManager />
                  </Suspense>
                ),
              },
              {
                path: "albums/trash",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AlbumTrash />
                  </Suspense>
                ),
              },
              {
                path: "media/versions",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminMediaVersions />
                  </Suspense>
                ),
              },
              {
                path: "customizations/catalog",
                element: (
                  <Suspense fallback={<Loader />}>
                    <CustomizationCatalog />
                  </Suspense>
                ),
              },
              {
                path: "orders",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminOrders />
                  </Suspense>
                ),
              },
              {
                path: "orders/:id",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminOrderDetail />
                  </Suspense>
                ),
              },
              {
                path: "promotions",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminPromotions />
                  </Suspense>
                ),
              },
              {
                path: "promotions/new",
                element: (
                  <Suspense fallback={<Loader />}>
                    <PromotionEdit />
                  </Suspense>
                ),
              },
              {
                path: "promotions/:promotionId/edit",
                element: (
                  <Suspense fallback={<Loader />}>
                    <PromotionEdit />
                  </Suspense>
                ),
              },
              {
                path: "production",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminProduction />
                  </Suspense>
                ),
              },
              {
                path: "production/:id",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminProductionDetail />
                  </Suspense>
                ),
              },
              // The old model-console production views are gone (their
              // features live on /admin/production); old bookmarks redirect.
              { path: "production/queue", element: <Navigate to="/admin/production" replace /> },
              { path: "production/groups", element: <Navigate to="/admin/production" replace /> },
              { path: "production/groups/:id", element: <RedirectToProduction /> },
              {
                path: "suppliers",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminSuppliers />
                  </Suspense>
                ),
              },
              {
                path: "products",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminProducts />
                  </Suspense>
                ),
              },
              {
                path: "products/new",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminProductCreate />
                  </Suspense>
                ),
              },
              {
                path: "products/:productId/edit",
                element: (
                  <Suspense fallback={<Loader />}>
                    <AdminProductEdit />
                  </Suspense>
                ),
              },
              {
                path: "settings/orders",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsOrders />
                  </Suspense>
                ),
              },
              // Retouch settings moved into the shop-wide order settings page.
              { path: "settings/retouch", element: <Navigate to="/admin/settings/orders" replace /> },
              // The sender-address form now lives at the bottom of /admin/shipping.
              { path: "settings/sender", element: <Navigate to="/admin/shipping" replace /> },
              {
                path: "settings/login",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsLogin />
                  </Suspense>
                ),
              },
              { path: "settings/album-types", element: (<Suspense fallback={<Loader />}><AlbumTypesSettings /></Suspense>) },
              { path: "settings/album-types/:id", element: (<Suspense fallback={<Loader />}><AlbumTypeDetail /></Suspense>) },
              { path: "pages",             element: (<Suspense fallback={<Loader />}><AdminPages /></Suspense>) },
              { path: "pages/new",         element: (<Suspense fallback={<Loader />}><AdminPageEdit /></Suspense>) },
              { path: "pages/:id",         element: (<Suspense fallback={<Loader />}><AdminPageEdit /></Suspense>) },
              { path: "shipping",          element: (<Suspense fallback={<Loader />}><AdminShipping /></Suspense>) },
              { path: "shipping/new",      element: (<Suspense fallback={<Loader />}><AdminShippingNew /></Suspense>) },
              { path: "shipping/:id/edit", element: (<Suspense fallback={<Loader />}><AdminShippingEdit /></Suspense>) },
              { path: "payments",          element: (<Suspense fallback={<Loader />}><AdminPayments /></Suspense>) },
              { path: "payments/new",      element: (<Suspense fallback={<Loader />}><AdminPaymentNew /></Suspense>) },
              { path: "payments/:id/edit", element: (<Suspense fallback={<Loader />}><AdminPaymentEdit /></Suspense>) },
              { path: "prints",            element: (<Suspense fallback={<Loader />}><AdminPrintJobs /></Suspense>) },
              { path: "print-templates",   element: (<Suspense fallback={<Loader />}><AdminPrintTemplates /></Suspense>) },
              { path: "print-jobs",        element: <Navigate to="/admin/prints" replace /> },
              { path: "invoices",                element: (<Suspense fallback={<Loader />}><AdminInvoices /></Suspense>) },
              { path: "invoices/settings",       element: (<Suspense fallback={<Loader />}><AdminInvoicingSettings /></Suspense>) },
              { path: "analytics",               element: (<Suspense fallback={<Loader />}><AdminAnalytics /></Suspense>) },
              { path: "reports",                 element: (<Suspense fallback={<Loader />}><AdminReports /></Suspense>) },
              { path: "bank-reconciliation",     element: (<Suspense fallback={<Loader />}><AdminBankReconciliation /></Suspense>) },
              { path: "audit/emails",            element: (<Suspense fallback={<Loader />}><AdminEmailAudit /></Suspense>) },
              { path: "audit/emails/:id",        element: (<Suspense fallback={<Loader />}><AdminEmailAuditDetail /></Suspense>) },
              {
                path: "settings/appearance",
                element: (
                  <Suspense fallback={<Loader />}>
                    <Appearance />
                  </Suspense>
                ),
              },
              {
                path: "settings/shop-state",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsShopState />
                  </Suspense>
                ),
              },
              {
                path: "settings/shop-basics",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsShopBasics />
                  </Suspense>
                ),
              },
              {
                path: "settings/languages",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsLanguages />
                  </Suspense>
                ),
              },
              {
                path: "settings/watermark",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsWatermark />
                  </Suspense>
                ),
              },
              {
                path: "settings/email",
                element: (
                  <Suspense fallback={<Loader />}>
                    <SettingsEmail />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
